import { styled } from "~/ui/style/stitches.config";
import {
  ReactElement,
  memo,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  useOthers,
  useSelf,
  useStorage,
  useUpdateMyPresence,
} from "../state/liveblocks.config";
import { CURSOR_COLORS } from "../ui/room/Cursors";
import { useSnapshot } from "valtio";
import Daily, { DailyCall } from "@daily-co/daily-js";
import {
  DailyProvider,
  useLocalSessionId,
  useMediaTrack,
  useDevices,
  useParticipantProperty,
  useParticipantIds,
  useVideoTrack,
  useDaily,
} from "@daily-co/daily-react";
import { AnimatePresence, motion } from "framer-motion";
import { AVControls, MuteIndicator } from "~/ui/menus/AVControls";
import { getRoom, updateRoom } from "~/api/roomApi";
import { useLocation } from "wouter";
import { MAX_ROOM_ATTENDEES } from "~/utils/consts";
import { localState } from "~/state/state";
import { sendAnalyticsEvent } from "~/utils/analytics";
import { AuthContext } from "~/utils/useAuth";
import { imageUrl } from "~/utils/imageurl";
import { trackEvent } from "~/api/analyticsApi";
import { RoomRecording } from "~/ui/room/RoomRecording";
import {
  sendErrorToast,
  sendToastMessageWithFallbackCode,
} from "~/utils/handleToasts";
import { isIOSNativeApp } from "~/utils/deviceDetection";
import { RouletteOverlay } from "~/ui/room/RouletteOverlay";
import { RouletteDisplayNames } from "~/ui/room/RouletteDisplayNames";
import { PopoutEffect, PopoutEffectOverlay, popoutEffectStyles, popoutEffectVariants } from "./PopoutEffects";

const DailyScreenRecording = ({
  callObject,
  roomId,
}: {
  callObject: DailyCall;
  roomId: string;
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState(""); // To store status updates
  const [audioLevel, setAudioLevel] = useState(0);

  useEffect(() => {
    (async () => {
      console.log("joining...");
      const room = await getRoom(roomId);
      if (!room) return;
      const { url } = room;
      await callObject.join({ url: url });
      console.log("joined");
      callObject.on("local-audio-level", (audioLevel) => {
        setAudioLevel(audioLevel.audioLevel);
      });
    })();
  }, [roomId]);

  const startScreenshare = async () => {
    callObject.startLocalAudioLevelObserver(100);
    try {
      await callObject.startScreenShare({
        displayMediaOptions: {
          selfBrowserSurface: "include",
        },
      });
    } catch (error) {
      sendErrorToast(1182);
    }
  };

  const stopScreenshare = async () => {
    try {
      await callObject.stopScreenShare();
    } catch (error) {
      console.error("Error stopping screenshare:", error);
    }
    callObject.stopLocalAudioLevelObserver();
  };

  const startRecording = async () => {
    try {
      const recordingResponse = await callObject.startRecording({
        width: 1280,
        height: 720,
        videoBitrate: 3000,
        fps: 25,
        backgroundColor: "#000000", // Black background
        layout: {
          preset: "custom",
          composition_params: {
            "videoSettings.preferScreenshare": true,
            mode: "single",
          },
        },
      });
      setRecordingStatus("Recording started...");
      setIsRecording(true);
      console.log("Recording started");
    } catch (error) {
      sendErrorToast(1181);
    }
  };

  const stopRecording = async () => {
    try {
      const response = await callObject.stopRecording();
      stopScreenshare();
      setRecordingStatus("Recording stopped. Processing...");
      setIsRecording(false);
    } catch (error) {
      setRecordingStatus("Error stopping recording");
      console.error("Error stopping recording:", error);
    }
  };

  return (
    <RoomRecording
      audioLevel={audioLevel}
      startScreenshare={async () => {
        await startScreenshare();
      }}
      stopScreenshare={async () => {
        await stopScreenshare();
      }}
      startRecording={async () => {
        await startRecording();
      }}
      stopRecording={async () => {
        await stopRecording();
      }}
    />
  );
};

export const AV = memo(function AV({
  isAVLoaded,
  setIsAVLoaded,
  roomId,
  isRecordingCall,
}: {
  isAVLoaded: boolean;
  setIsAVLoaded: Function;
  roomId: string;
  isRecordingCall: boolean;
}) {
  // Early return for iOS native - disable AV entirely
  if (isIOSNativeApp()) {
    useEffect(() => {
      setIsAVLoaded(true);
      localState.isConnecting = false;
    }, []);
    
    return <></>;
  }

  const numOthers = useOthers((others) => others.length);
  const otherConnectionIds = useOthers((others) =>
    others.map((other) => other.connectionId)
  );
  const myColor = useSelf((self) => self.presence.color);
  const fallbackColor = "hsla(0, 0%, 68%, 1)";
  const connectionId = useSelf((me) => me.connectionId);
  const colorOffset: number = roomId.charCodeAt(0);
  const [_, setLocation] = useLocation();
  const [callObject, setCallObject] = useState<DailyCall | null>(null);
  const [isJoined, setIsJoined] = useState(false);
  const [userId, setUserId] = useState("");
  const [displayName, setdisplayName] = useState("");
  const [userName, setUserName] = useState("");
  const [profilePic, setProfilePic] = useState("");
  const [previousNumUsers, setNumUsers] = useState(1);
  const updateMyPresence = useUpdateMyPresence();
  const { user, updateUser } = useContext(AuthContext);
  const [isRouletteRoom, setIsRouletteRoom] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const isWaitingForMatch = isRouletteRoom && otherConnectionIds.length === 0;
  const [leaveCallTimer, setLeaveCallTimer] = useState<NodeJS.Timeout | null>(null);

  const callObjectRef = useRef<DailyCall | null>(null);

  const setCameraOffAndMicMuted = (isCameraOff: boolean, isMuted: boolean) => {
    if (!callObject) return;
    const newSetting = {
      setVideo: !isCameraOff,
      setAudio: !isMuted,
    };
    callObject.updateParticipant("local", newSetting);
    updateUser({ isMuted, isCameraOff });
  };

  useEffect(() => {
    if (otherConnectionIds.length > 0) {
      trackEvent("/room/someone_joined", {
        roomId: roomId,
      });
      
      // Play connection sound effect
      const audio = new Audio('/sounds/connected.wav');
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Audio play failed:', e));
    }
    if (otherConnectionIds.length === 0) {
      updateMyPresence({
        color: CURSOR_COLORS[colorOffset % CURSOR_COLORS.length],
      });
      return;
    }
    if (isJoined && callObject) {
      const sortedConnectionIds = [...otherConnectionIds];
      sortedConnectionIds.push(connectionId);
      sortedConnectionIds.sort((a, b) => a - b);
      const newColor =
        CURSOR_COLORS[
          (colorOffset + sortedConnectionIds.indexOf(connectionId)) %
            CURSOR_COLORS.length
        ];
      updateMyPresence({
        color: newColor,
      });
    }
  }, [isJoined, otherConnectionIds.length]);

  useEffect(() => {
    (async () => {
      if (user?.uuid && callObject) {
        if (isJoined) {
          callObject.setUserData({
            connectionId: connectionId,
            userName: user?.username ? user?.username : "",
            displayName: user?.name ? user?.name : "",
            profilePic: user.animalPic
              ? user.animalPic
              : user?.profilePic
              ? user?.profilePic
              : "",
            userId: user?.uuid ? user?.uuid : "",
            color: myColor,
          });
        }

        if (user.profilePic) {
          setProfilePic(user.animalPic ? user.animalPic : user.profilePic);
          setUserId(user.uuid);
          setdisplayName(user.name);
          setUserName(user.username);
        }
      }
    })();
  }, [user, callObject, isJoined, myColor]);

  useEffect(() => {
    (async () => {
      const numPresent = callObject?.participantCounts().present;
      if (numPresent) {
        if (numPresent > previousNumUsers) {
          sendAnalyticsEvent("room_gained_user", {
            room_num_users: numPresent,
            room_name: roomId,
          });
        } else if (numPresent < previousNumUsers) {
          sendAnalyticsEvent("room_lost_user", {
            room_num_users: numPresent,
            room_name: roomId,
          });
        }
        setNumUsers(numPresent);
        await updateRoom(roomId, numPresent);
      }
    })();
  }, [callObject?.participantCounts().present]);

  useEffect(() => {
    if (!callObject || !user) return;
    (async () => {
      const room = await getRoom(roomId);
      setIsAVLoaded(true);
      localState.isConnecting = false;

      if (numOthers > 0) {
        if (!room) {
          localState.isConnecting = false;
          setLocation("/room404");
          return;
        }
        if (
          !isJoined &&
          room.numParticipants &&
          room.numParticipants > MAX_ROOM_ATTENDEES - 1
        ) {
          localState.isConnecting = false;
          return;
        }
        const { url } = room;
        if (!isJoined) {
          await callObject.join({ url: url }).then(() => {
            setCameraOffAndMicMuted(user.isCameraOff, user.isMuted);
            setIsJoined(true);
            
            // Start audio monitoring after joining
            callObject.startLocalAudioLevelObserver(100);
            callObject.on("local-audio-level", (event) => {
              setAudioLevel(event.audioLevel);
            });
          });
        }
      } else {
        if (!isJoined) {
          await callObject.startCamera();
          setCameraOffAndMicMuted(user.isCameraOff, user.isMuted);
          
          // Use native browser audio monitoring when alone
          if (room?.isRouletteRoom) {
            navigator.mediaDevices.getUserMedia({ audio: true })
              .then((stream) => {
                const audioContext = new AudioContext();
                const analyser = audioContext.createAnalyser();
                const microphone = audioContext.createMediaStreamSource(stream);
                const dataArray = new Uint8Array(analyser.frequencyBinCount);
                
                microphone.connect(analyser);
                analyser.fftSize = 256;
                
                const updateAudioLevel = () => {
                  analyser.getByteFrequencyData(dataArray);
                  const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                  const normalizedLevel = Math.min(average / 32, 1); // Even more sensitive
                  setAudioLevel(normalizedLevel);
                  requestAnimationFrame(updateAudioLevel);
                };
                
                updateAudioLevel();
              })
              .catch(error => {
                console.error('Mic permissions denied:', error);
              });
          }
        }
      }
    })();
  }, [numOthers, callObject, isJoined]);

  useEffect(() => {
    (async () => {
      if (callObject) return;
      // Type: https://docs.daily.co/reference/rest-api/rooms
      const call = Daily.createCallObject({
        userData: {
          connectionId: connectionId,
        },
      });

      // Get and join the room
      const room = await getRoom(roomId);
      if (!room) setLocation("/room404");
      else {
        const { url } = room;
        setCallObject(call);
        callObjectRef.current = call;
      }
    })();
  }, [user, roomId, callObject]);

  const leaveCall = async () => {
    await callObject?.leave();
  };

  useEffect(() => {
    return () => {
      (async () => await leaveCall())();
    };
  }, [callObject]);

  useEffect(() => {
    let joinTimestamp = Date.now();
    return () => {
      if (joinTimestamp && joinTimestamp > 0) {
        let now = Date.now();
        let sessionLength = now - joinTimestamp;
        // Don't trigger unless they've stayed in the room longer than 5 seconds
        // This will eliminate when react sometimes reloads the AV component as the room is loading
        if (sessionLength > 5000) {
          sendAnalyticsEvent("leave_room", {
            room_duration_ms: sessionLength,
            room_num_users: numOthers || 0,
            room_name: roomId,
          });
        }
      }
    };
  }, []);

  useEffect(() => {
    (async () => {
      const room = await getRoom(roomId);
      const isRoulette = room?.isRouletteRoom || false;
      setIsRouletteRoom(isRoulette);
      // Remove this line since it's now set in Room.tsx:
      // localState.isRouletteRoom = isRoulette;
    })();
  }, [roomId]);

  // Add this to see the final state
  useEffect(() => {
    console.log("isWaitingForMatch:", isWaitingForMatch);
  }, [isWaitingForMatch]);

  useEffect(() => {
    if (!callObject || !isJoined) return;
    
    // Start monitoring remote participants' audio levels
    callObject.startRemoteParticipantsAudioLevelObserver(100);
    
    return () => {
      callObject.stopRemoteParticipantsAudioLevelObserver();
    };
  }, [callObject, isJoined]);

  useEffect(() => {
    if (numOthers === 0 && isJoined && callObject) {
      const timer = setTimeout(() => {
        callObject.leave();
        setIsJoined(false);
        setLeaveCallTimer(null);
      }, 30000);
      
      setLeaveCallTimer(timer);
    } else if (numOthers > 0 && leaveCallTimer) {
      clearTimeout(leaveCallTimer);
      setLeaveCallTimer(null);
    }
    
    return () => {
      if (leaveCallTimer) {
        clearTimeout(leaveCallTimer);
      }
    };
  }, [numOthers, isJoined, callObject]);

  // Add this effect to clean up when isInRoom becomes false
  useEffect(() => {
    if (!localState.isInRoom && callObject && isJoined) {
      callObject.leave();
      setIsJoined(false);
    }
  }, [localState.isInRoom, callObject, isJoined]);

  return !isAVLoaded ? (
    <></>
  ) : callObject ? (
    <DailyProvider callObject={callObject}>
      {isRecordingCall && (
        <DailyScreenRecording callObject={callObject} roomId={roomId} />
      )}
      <RouletteOverlayWithData
        isRouletteRoom={isRouletteRoom}
        otherConnectionIds={otherConnectionIds}
        audioLevel={audioLevel}
      />
      <RouletteDisplayNames
        isRouletteRoom={isRouletteRoom}
        selfDisplayName={displayName}
        otherConnectionIds={otherConnectionIds}
      />
      <AVControls
        useDevices={useDevices}
        setCameraOffAndMicMuted={setCameraOffAndMicMuted}
        isPopoutEnabled={!isRouletteRoom}
        myConnectionId={connectionId}
      />
      <PopoutVideoDisplay
        isRecordingCall={isRecordingCall}
        isRouletteRoom={isRouletteRoom}
        myConnectionId={connectionId}
        profilePic={profilePic}
        displayName={displayName}
        userId={userId}
        userName={userName}
        color={myColor ? myColor : fallbackColor}
        isJoined={isJoined}
      />
      <StyledAVContainer
        style={{
          transform: isRecordingCall ? "scale(1.6) translateY(12px)" : isRouletteRoom ? "scale(1.2) translateY(8px)" : "",
        }}
      >
        <SelfStreamWithPopoutAwareness
          callObject={callObject}
          profilePic={profilePic}
          displayName={displayName}
          userId={userId}
          userName={userName}
          color={myColor ? myColor : fallbackColor}
          isJoined={isJoined}
          myConnectionId={connectionId}
          isRecordingCall={isRecordingCall}
          isRouletteRoom={isRouletteRoom}
        />
        {isWaitingForMatch && callObject && (
          <StyledStream
            style={{
              background: "linear-gradient(rgb(186 186 186 / 35%), transparent)",
              borderColor: CURSOR_COLORS[(colorOffset + 1) % CURSOR_COLORS.length],
              position: "relative",
            }}
            className="sentry-block"
          >
            <img
              src="/images/candle-monochrome.gif"
              alt="Finding match"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "92px",
                height: "92px",
                objectFit: "contain",
                borderRadius: "50%",
              }}
            />
          </StyledStream>
        )}
        <OtherStreamsWithPopoutAwareness
          isRecordingCall={isRecordingCall}
          isRouletteRoom={isRouletteRoom}
        />
      </StyledAVContainer>
    </DailyProvider>
  ) : (
    <StyledAVContainer onClick={(e) => e.stopPropagation()}>
      {self && (
        <StyledStream
          style={{
            background: `linear-gradient(${
              myColor ? myColor : fallbackColor
            }, transparent)`,
            borderColor: myColor ? myColor : fallbackColor,
          }}
          className="sentry-block"
        />
      )}
      {otherConnectionIds.map((connectionId, i) => (
        <StyledStream
          key={connectionId}
          style={{
            background: `linear-gradient(transparent, transparent)`,
            borderColor: "transparent",
          }}
          className="sentry-block"
        />
      ))}
    </StyledAVContainer>
  );
});

const OtherStreams = memo(function OtherStreams() {
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });
  return (
    <>
      <AnimatePresence>
        {remoteParticipantIds.map((participantId) => (
          <Stream id={participantId} key={participantId} />
        ))}
      </AnimatePresence>
    </>
  );
});

// Popout video display - renders the popped out video in the center of the screen
const PopoutVideoDisplay = memo(function PopoutVideoDisplay({
  isRouletteRoom,
  myConnectionId,
  profilePic,
  displayName,
  color,
}: {
  isRecordingCall: boolean;
  isRouletteRoom: boolean;
  myConnectionId: number;
  profilePic: string;
  displayName: string;
  userId: string;
  userName: string;
  color: string;
  isJoined: boolean;
}) {
  const popoutVideoConnectionId = useStorage((root) => root.popoutVideoConnectionId);
  const popoutVideoEffect = useStorage((root) => root.popoutVideoEffect);
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });

  // Don't show popout in roulette mode
  if (isRouletteRoom) {
    return null;
  }

  // No one is popped out
  if (popoutVideoConnectionId === null) {
    return null;
  }

  // Check if it's my video that's popped out
  const isMyVideoPopped = popoutVideoConnectionId === myConnectionId;

  if (isMyVideoPopped) {
    return (
      <PopoutSelfVideo
        profilePic={profilePic}
        displayName={displayName}
        color={color}
        effect={popoutVideoEffect}
      />
    );
  }

  // Find the remote participant whose video is popped out
  // We need to check each participant's connectionId in their userData
  return (
    <PopoutRemoteVideo
      remoteParticipantIds={remoteParticipantIds}
      popoutVideoConnectionId={popoutVideoConnectionId}
      effect={popoutVideoEffect}
    />
  );
});

// Component to render self video in popout mode
const PopoutSelfVideo = memo(function PopoutSelfVideo({
  profilePic,
  displayName,
  color,
  effect,
}: {
  profilePic: string;
  displayName: string;
  color: string;
  effect: PopoutEffect;
}) {
  const localParticipantSessionId = useLocalSessionId();
  const localParticipantVideoTrack = useVideoTrack(localParticipantSessionId || "");
  const localVideoElement = useRef<HTMLVideoElement>(null);
  const localParticipantAudio = useParticipantProperty(localParticipantSessionId, "audio");
  const localParticipantVideo = useParticipantProperty(localParticipantSessionId, "video");
  const localParticipantUserData = useParticipantProperty(localParticipantSessionId, "userData");

  useEffect(() => {
    if (!localParticipantVideoTrack.persistentTrack || !localVideoElement.current) return;
    localVideoElement.current.srcObject =
      (localParticipantVideoTrack.persistentTrack &&
        new MediaStream([localParticipantVideoTrack?.persistentTrack])) ||
      null;
  }, [localParticipantVideoTrack.persistentTrack]);

  if (!localParticipantSessionId) return null;

  return (
    <StyledPopoutContainer>
      <StyledPopoutVideo
        style={{
          backgroundImage: `url(${imageUrl(
            `/profile-pics/${profilePic ? profilePic : (localParticipantUserData as any)?.profilePic}`,
            400
          )})`,
          borderColor: color,
        }}
        isCameraOff={!localParticipantVideo}
        effect={effect}
      >
        <PopoutEffectOverlay effect={effect} />
        {!localParticipantAudio && <MuteIndicator />}
        <video autoPlay muted playsInline ref={localVideoElement} />
        <div className="popout-name">{displayName || "you"}</div>
      </StyledPopoutVideo>
    </StyledPopoutContainer>
  );
});

// Component to find and render remote participant's video in popout mode
const PopoutRemoteVideo = memo(function PopoutRemoteVideo({
  remoteParticipantIds,
  popoutVideoConnectionId,
  effect,
}: {
  remoteParticipantIds: string[];
  popoutVideoConnectionId: number;
  effect: PopoutEffect;
}) {
  // Find the participant with matching connectionId
  // This component will check each participant
  return (
    <>
      {remoteParticipantIds.map((participantId) => (
        <PopoutRemoteVideoCheck
          key={participantId}
          participantId={participantId}
          popoutVideoConnectionId={popoutVideoConnectionId}
          effect={effect}
        />
      ))}
    </>
  );
});

// Check if this remote participant is the one that should be popped out
const PopoutRemoteVideoCheck = memo(function PopoutRemoteVideoCheck({
  participantId,
  popoutVideoConnectionId,
  effect,
}: {
  participantId: string;
  popoutVideoConnectionId: number;
  effect: PopoutEffect;
}) {
  const participantUserData = useParticipantProperty(participantId, "userData");
  const participantConnectionId = (participantUserData as any)?.connectionId;

  // Only render if this is the popped out participant
  if (participantConnectionId !== popoutVideoConnectionId) {
    return null;
  }

  return <PopoutRemoteVideoStream participantId={participantId} effect={effect} />;
});

// Render the actual remote video stream in popout mode
const PopoutRemoteVideoStream = memo(function PopoutRemoteVideoStream({
  participantId,
  effect,
}: {
  participantId: string;
  effect: PopoutEffect;
}) {
  const videoTrack = useMediaTrack(participantId, "video");
  const audioTrack = useMediaTrack(participantId, "audio");
  const videoElement = useRef<HTMLVideoElement>(null);
  const audioElement = useRef<HTMLAudioElement>(null);
  const lastVT = useRef<any>(null);
  const lastAT = useRef<any>(null);

  const participantAudio = useParticipantProperty(participantId, "audio");
  const participantVideo = useParticipantProperty(participantId, "video");
  const participantUserData = useParticipantProperty(participantId, "userData");

  useEffect(() => {
    if (
      videoTrack?.state === "playable" &&
      videoElement.current &&
      videoTrack.persistentTrack &&
      lastVT.current?.persistentTrack?.id !== videoTrack.persistentTrack.id
    ) {
      videoElement.current.srcObject = new MediaStream([videoTrack.persistentTrack]);
      lastVT.current = videoTrack;
    }
  }, [videoTrack]);

  useEffect(() => {
    if (lastAT.current === audioTrack) return;
    if (
      audioTrack?.state === "playable" &&
      audioElement.current &&
      audioTrack.persistentTrack &&
      lastAT.current?.persistentTrack?.id !== audioTrack.persistentTrack.id
    ) {
      audioElement.current.srcObject = new MediaStream([audioTrack.persistentTrack]);
      lastAT.current = audioTrack;
    }
  }, [audioTrack]);

  const color = (participantUserData as any)?.color || "transparent";
  const displayName = (participantUserData as any)?.displayName || "";

  return (
    <StyledPopoutContainer>
      <StyledPopoutVideo
        style={{
          backgroundImage: `url("${imageUrl(
            `/profile-pics/${(participantUserData as any)?.profilePic}`,
            400
          )}")`,
          borderColor: color,
        }}
        isCameraOff={!participantVideo}
        effect={effect}
      >
        <PopoutEffectOverlay effect={effect} />
        {!participantAudio && <MuteIndicator />}
        {videoTrack && <video autoPlay muted playsInline ref={videoElement} />}
        {audioTrack && <audio autoPlay playsInline ref={audioElement} />}
        <div className="popout-name">{displayName}</div>
      </StyledPopoutVideo>
    </StyledPopoutContainer>
  );
});

const OtherStreamsWithPopoutAwareness = memo(function OtherStreamsWithPopoutAwareness({
  isRecordingCall,
  isRouletteRoom,
}: {
  isRecordingCall: boolean;
  isRouletteRoom: boolean;
}) {
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });
  const popoutVideoConnectionId = useStorage((root) => root.popoutVideoConnectionId);
  const isPopoutActive = !isRouletteRoom && popoutVideoConnectionId !== null;

  return (
    <>
      <AnimatePresence>
        {remoteParticipantIds.map((participantId) => {
          // Get the participant's user data to check their connectionId
          return (
            <StreamWithPopoutCheck
              id={participantId}
              key={participantId}
              popoutVideoConnectionId={popoutVideoConnectionId}
              isPopoutActive={isPopoutActive}
            />
          );
        })}
      </AnimatePresence>
    </>
  );
});

const SelfStream = memo(function SelfStream({
  profilePic,
  displayName,
  userId,
  userName,
  color,
  isJoined,
}: {
  profilePic: string;
  displayName: string;
  userId: string;
  userName: string;
  callObject: DailyCall;
  color: string;
  isJoined: boolean;
}) {
  const [selfAudioLevel, setSelfAudioLevel] = useState(0);
  const callObject = useDaily();
  
  // Reset audio level when not joined
  useEffect(() => {
    if (!isJoined) {
      setSelfAudioLevel(0);
    }
  }, [isJoined]);

  // Track local audio level
  useEffect(() => {
    if (!callObject) {
      setSelfAudioLevel(0); // Reset when no call object
      return;
    }
    
    const handleLocalAudioLevel = (event: any) => {
      setSelfAudioLevel(event.audioLevel || 0);
    };
    
    callObject.on("local-audio-level", handleLocalAudioLevel);
    
    return () => {
      callObject.off("local-audio-level", handleLocalAudioLevel);
    };
  }, [callObject]);

  /* This is for displaying our self-view. */
  const localParticipantSessionId = useLocalSessionId();
  const localParticipantVideoTrack = useVideoTrack(
    localParticipantSessionId || ""
  );

  const localVideoElement = useRef<HTMLVideoElement>(null);
  const localParticipantAudio = useParticipantProperty(
    localParticipantSessionId,
    "audio"
  );

  const localParticipantVideo = useParticipantProperty(
    localParticipantSessionId,
    "video"
  );
  const localParticipantUserData = useParticipantProperty(
    localParticipantSessionId,
    "userData"
  );

  useEffect(() => {
    if (
      !localParticipantVideoTrack.persistentTrack ||
      !localVideoElement.current
    )
      return;
    localVideoElement.current.srcObject =
      (localParticipantVideoTrack.persistentTrack &&
        new MediaStream([localParticipantVideoTrack?.persistentTrack])) ||
      null;
  }, [localParticipantVideoTrack.persistentTrack]);

  if (!localParticipantSessionId) return null;

  return (
    <StreamContainer
      layoutId={"stream-self"}
      isMuted={!localParticipantAudio}
      isCameraOff={!localParticipantVideo}
      imgUrl={`url(${imageUrl(
        `/profile-pics/${
          profilePic
            ? profilePic
            : (localParticipantUserData as any)?.profilePic
        }`,
        200
      )})`}
      color={color}
      videoElement={
        <video autoPlay muted playsInline ref={localVideoElement} />
      }
      displayName={displayName ? displayName : "you"}
      userId={userId ? userId : ""}
      userName={userName}
      isSelfStream={true}
      audioLevel={selfAudioLevel}
    ></StreamContainer>
  );
});

// Wrapper that hides self stream when user has their video popped out
const SelfStreamWithPopoutAwareness = memo(function SelfStreamWithPopoutAwareness({
  profilePic,
  displayName,
  userId,
  userName,
  color,
  isJoined,
  myConnectionId,
  isRecordingCall,
  isRouletteRoom,
}: {
  profilePic: string;
  displayName: string;
  userId: string;
  userName: string;
  callObject: DailyCall;
  color: string;
  isJoined: boolean;
  myConnectionId: number;
  isRecordingCall: boolean;
  isRouletteRoom: boolean;
}) {
  const popoutVideoConnectionId = useStorage((root) => root.popoutVideoConnectionId);

  // Don't render in thumbnail if this user is popped out
  const isMyVideoPopped = !isRouletteRoom && popoutVideoConnectionId === myConnectionId;

  if (isMyVideoPopped) {
    return null;
  }

  return (
    <SelfStream
      callObject={null as any}
      profilePic={profilePic}
      displayName={displayName}
      userId={userId}
      userName={userName}
      color={color}
      isJoined={isJoined}
    />
  );
});

// Stream component that checks if the participant is popped out
const StreamWithPopoutCheck = memo(function StreamWithPopoutCheck({
  id,
  popoutVideoConnectionId,
  isPopoutActive,
}: {
  id: string;
  popoutVideoConnectionId: number | null;
  isPopoutActive: boolean;
}) {
  const participantUserData = useParticipantProperty(id, "userData");
  const participantConnectionId = (participantUserData as any)?.connectionId;

  // Don't render in thumbnail if this participant is popped out
  if (isPopoutActive && participantConnectionId === popoutVideoConnectionId) {
    return null;
  }

  return <Stream id={id} />;
});

const StreamContainer = memo(function StreamContainer({
  layoutId,
  imgUrl,
  color,
  videoElement,
  audioElement,
  displayName,
  userId,
  userName,
  isMuted,
  isCameraOff,
  isSelfStream,
  audioLevel = 0,
}: {
  layoutId: string;
  imgUrl: string;
  color: string;
  isMuted: boolean;
  isCameraOff: boolean;
  videoElement: ReactElement;
  audioElement?: ReactElement;
  displayName?: string;
  userId?: string;
  userName?: string;
  isSelfStream?: boolean;
  audioLevel?: number;
}) {
  const [favoritesUpdated, setFavoritesUpdated] = useState(true);
  const [faved, setFaved] = useState(false);
  const [isShowingButtons, setIsShowingButtons] = useState(false);
  const { isOnMobile, isMobileMessageActive } = useSnapshot(localState);

  const { user, addOrRemoveFavorite } = useContext(AuthContext);

  useEffect(() => {
    if (!userId || !user) return;
    (async () => {
      setFavoritesUpdated(false);
      const imf = user.favorites.indexOf(userId) >= 0;
      setFaved(imf);
    })();
  }, [userId]);
  useEffect(() => {
    if (!userId || !favoritesUpdated) return;
    setFaved(!faved);
    setFavoritesUpdated(false);
  }, [favoritesUpdated]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      setIsShowingButtons(false);
    };

    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, []);

  const speakingIntensity = (audioLevel > 0.01 && !isMuted) ? audioLevel : 0;
  const ringSize = speakingIntensity > 0 ? (speakingIntensity > 0.2 ? 4 : 2) : 0;

  // Remove debug audio levels logging

  return (
    <StyledStream
      id={`user-stream-${userName}`}
      layoutId={layoutId}
      isCameraOff={isCameraOff}
      isShowingButtons={isShowingButtons}
      isMobileMessageActive={isMobileMessageActive}
      onClick={(e) => {
        if (isOnMobile) {
          setIsShowingButtons(!isShowingButtons);
        }
        e.stopPropagation();
      }}
      onPointerOver={(e) => {
        if (!isOnMobile) {
          setIsShowingButtons(true);
        }
        e.stopPropagation();
      }}
      onPointerOut={(e) => {
        if (!isOnMobile) {
          setIsShowingButtons(false);
        }
        e.stopPropagation();
      }}
      style={{
        position: "relative",
        backgroundImage: imgUrl,
        backgroundSize: "cover",
        backgroundPosition: "50% 50%",
        borderColor: color,
        borderWidth: "2px",
        boxShadow: speakingIntensity > 0 ? 
          `0 0 0 ${ringSize}px ${color}50` : "none", 
        transition: "box-shadow 0.3s ease",
      }}
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      className="sentry-block user-stream"
    >
      <>
        {isMuted ? <MuteIndicator /> : ""}
        {videoElement}
        {audioElement}
        {userId && (
          <div className="user-buttons-container">
            {!isSelfStream && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  (async () => {
                    const res = await addOrRemoveFavorite(userId).then(() =>
                      setFavoritesUpdated(true)
                    );
                  })();
                }}
                className="fave-button btn"
              >
                {faved ? (
                  <img src="/images/room/heart-full.svg"></img>
                ) : (
                  <img src="/images/room/heart.svg"></img>
                )}
                <div className="tooltip">
                  <img className="point" src="/images/room/point.svg"></img>
                  {!faved ? "Save profile" : "Saved"}
                </div>
              </div>
            )}
            <a
              className="profile-button btn"
              href={`${
                window.location.protocol +
                "//" +
                window.location.host +
                "/profile/" +
                userName
              }`}
              target="_blank"
            >
              <img src="/images/room/link-out.svg"></img>
              <div className="tooltip">
                <img className="point" src="/images/room/point.svg"></img>
                {"View profile"}
              </div>
            </a>
          </div>
        )}
        {displayName && (
          <div className="user-name-container-container">
            <div className="user-name-container">
              <img className="point" src="/images/room/point.svg" />
              <div className="user-name">{displayName}</div>{" "}
            </div>
          </div>
        )}
      </>
    </StyledStream>
  );
});

const Stream = memo(function Stream({ id }: { id: string }) {
  const videoTrack = useMediaTrack(id, "video");
  const audioTrack = useMediaTrack(id, "audio");
  const [audioLevel, setAudioLevel] = useState(0);
  const callObject = useDaily();
  
  const videoElement = useRef<HTMLVideoElement>(null);
  const audioElement = useRef<HTMLAudioElement>(null);
  const lastVT = useRef<any>(null);
  const lastAT = useRef<any>(null);
  
  // Track audio level for this participant
  useEffect(() => {
    if (!callObject) return;
    
    const handleRemoteAudioLevel = (event: any) => {
      if (event.participantsAudioLevel && event.participantsAudioLevel[id]) {
        const audioLevel = event.participantsAudioLevel[id];
        setAudioLevel(audioLevel || 0);
      }
    };
    
    callObject.on("remote-participants-audio-level", handleRemoteAudioLevel);
    
    return () => {
      callObject.off("remote-participants-audio-level", handleRemoteAudioLevel);
    };
  }, [callObject, id]);

  useEffect(() => {
    // HACK: For some reason videoTrack rerenders every time cursor moves, track this referential equality and only mount new stream if it changes
    /*  The track is ready to be played. We can show video of the remote participant in the UI.*/
    if (
      videoTrack?.state === "playable" &&
      videoElement.current &&
      videoTrack.persistentTrack &&
      lastVT.current?.persistentTrack?.id !== videoTrack.persistentTrack.id
    ) {
      videoElement.current.srcObject = new MediaStream([
        videoTrack.persistentTrack,
      ]);
      lastVT.current = videoTrack;
    }
  }, [videoTrack]);

  useEffect(() => {
    // HACK: For some reason videoTrack rerenders every time cursor moves, track this referential equality and only mount new stream if it changes
    if (lastAT.current === audioTrack) return;
    if (
      audioTrack?.state === "playable" &&
      audioElement.current &&
      audioTrack.persistentTrack &&
      lastAT.current?.persistentTrack?.id !== audioTrack.persistentTrack.id
    ) {
      audioElement.current.srcObject = new MediaStream([
        audioTrack.persistentTrack,
      ]);
      lastAT.current = audioTrack;
    }
  }, [audioTrack]);

  const participantAudio = useParticipantProperty(id, "audio");
  const participantVideo = useParticipantProperty(id, "video");
  const participantUserData = useParticipantProperty(id, "userData");

  return (
    <StreamContainer
      layoutId={"stream" + id}
      isMuted={!participantAudio}
      isCameraOff={!participantVideo}
      imgUrl={`url("${imageUrl(
        `/profile-pics/${(participantUserData as any)?.profilePic}`,
        200
      )}")`}
      color={
        (participantUserData as any)?.color
          ? (participantUserData as any)?.color
          : "transparent"
      }
      videoElement={
        videoTrack ? (
          <video autoPlay muted playsInline ref={videoElement} />
        ) : (
          <></>
        )
      }
      audioElement={
        audioTrack ? <audio autoPlay playsInline ref={audioElement} /> : <></>
      }
      displayName={
        (participantUserData as any)?.displayName
          ? (participantUserData as any)?.displayName
          : ""
      }
      userId={
        (participantUserData as any)?.userId
          ? (participantUserData as any)?.userId
          : null
      }
      userName={
        (participantUserData as any)?.userName
          ? (participantUserData as any)?.userName
          : null
      }
      audioLevel={audioLevel}
    ></StreamContainer>
  );
});

const StyledAVContainer = styled("div", {
  position: "fixed",
  inset: "1rem",
  bottom: "auto",
  display: "flex",
  gap: "1rem",
  "@mobile": {
    gap: "0.5rem",
  },
  placeContent: "center",
  zIndex: "1",
  pointerEvents: "none",
});

const StyledStream = styled(motion.div, {
  pointerEvents: "all",
  width: "6rem",
  height: "6rem",
  background: "rgba(0,0,0,.1)",
  borderRadius: "999rem",
  border: "2px solid rgba(0,0,0,.5)",
  position: "relative",
  marginTop: "$navHeight",
  cursor: "pointer",

  "& video": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "999rem",
    transform: "rotateY(180deg)",
    filter: "grayscale(1)",
  },
  "& .user-buttons-container": {
    width: "58px",
    borderRadius: "9px",
    "@mobile": {
      width: "64px",
    },
    backgroundColor: "$wash",
    position: "absolute",
    display: "flex",
    height: "30px",
    alignItems: "center",
    justifyContent: "center",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    margin: "auto",

    "& .btn": {
      height: "24px",
      width: "24px",

      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "2px",
      "@mobile": {
        transform: "scale(1.2)",
        padding: "6px",
      },
      "&:hover": {
        "@notmobile": {
          backgroundColor: "$gray200",
        },

        "& .tooltip": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      },
      "& .tooltip": {
        padding: "4px 0 4px 0",
        position: "absolute",
        fontSize: "10px",
        width: "80px",
        bottom: "40px",
        backgroundColor: "black",
        textAlign: "center",
        color: "white",
        display: "none",
        borderRadius: "6px",
        "& .point": {
          position: "absolute",
          bottom: "-10px",
          left: 0,
          right: 0,
          margin: "auto",
          width: "12px",
          transform: "rotate(90deg)",
        },
      },
      "& img": {
        height: "20px",
        width: "20px",
      },
    },
  },
  "& .user-name-container-container": {
    display: "flex",
    justifyContent: "center",
    width: "50px",
    height: "2px",
    position: "absolute",
    zIndex: "3",
    left: 0,
    right: 0,
    margin: "auto",

    "& .user-name-container": {
      display: "flex",
      flexDirection: "column",
      "& .user-name": {
        backgroundColor: "black",
        borderRadius: "9px",
        top: "8px",
        color: "white",
        marginTop: "-3px",
        padding: "4px 10px 6px 10px",
        fontSize: "12px",
        fontWeight: "200",
        textAlign: "center",
        width: "auto",
        whiteSpace: "nowrap",
      },
      "& .point": {
        transform: "rotate(-90deg)",
        alignSelf: "center",
      },
    },
  },
  variants: {
    isCameraOff: {
      true: {
        "& video": {
          opacity: 0,
        },
      },
    },
    isShowingButtons: {
      true: {},
      false: {
        "& .user-name-container-container, & .user-buttons-container": {
          display: "none",
        },
      },
    },
    isMobileMessageActive: {
      true: {
        marginTop: "85px",
      },
    },
  },
});

// Styled components for popout video display
const StyledPopoutContainer = styled("div", {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 2,
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledPopoutVideo = styled(motion.div, {
  pointerEvents: "all",
  width: "500px",
  height: "400px",
  "@mobile": {
    width: "270px",
    height: "270px",
  },
  background: "rgba(0,0,0,.1)",
  backgroundSize: "cover",
  backgroundPosition: "50% 50%",
  borderRadius: "24px",
  border: "3px solid rgba(0,0,0,.5)",
  position: "relative",
  cursor: "default",
  transition: "box-shadow 0.3s ease",
  boxShadow: "0 8px 12px rgba(0,0,0,0.1)",

  "& video": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "21px",
    transform: "rotateY(0deg)",
    filter: "saturate(.8) contrast(1.1)",
  },

  "& .popout-name": {
    position: "absolute",
    bottom: "6px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(0,0,0,0.5)",
    color: "white",
    padding: "4px 14px",
    borderRadius: "18px",
    fontSize: "10px",
    fontWeight: "400",
    whiteSpace: "nowrap",
    zIndex: 2,
  },

  // Mute indicator override for popout - target the mute-indicator class
  "& .mute-indicator": {
    bottom: "6px",
    right: "6px",
    height: "28px",
    width: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },

  // Import effect styles from PopoutEffects
  ...popoutEffectStyles,

  variants: {
    isCameraOff: {
      true: {
        "& video": {
          opacity: 0,
        },
      },
    },
    ...popoutEffectVariants,
  },
});

const RouletteOverlayWithData = ({
  isRouletteRoom, 
  otherConnectionIds, 
  audioLevel 
}: {
  isRouletteRoom: boolean;
  otherConnectionIds: number[];
  audioLevel: number;
}) => {
  const remoteParticipantIds = useParticipantIds({ filter: "remote" });
  const firstRemoteParticipant = useParticipantProperty(
    remoteParticipantIds.length > 0 ? remoteParticipantIds[0] : "", 
    "userData"
  );
  const matchedUserData = firstRemoteParticipant as any;

  // Store matched username in local state for the exit survey
  useEffect(() => {
    if (matchedUserData?.userName) {
      localState.matchedUsername = matchedUserData.userName;
    }
  }, [matchedUserData?.userName]);

  // Track when user gets matched
  useEffect(() => {
    if (isRouletteRoom && otherConnectionIds.length > 0 && matchedUserData?.userName) {
      trackEvent("/room/roulette/matched", {
        match: matchedUserData.userName,
        room: window.location.pathname.split('/')[2]
      });
    }
  }, [isRouletteRoom, otherConnectionIds.length, matchedUserData?.userName]);

  return (
    <RouletteOverlay 
      isRouletteRoom={isRouletteRoom}
      otherConnectionIds={otherConnectionIds}
      matchedDisplayName={matchedUserData?.displayName}
      matchedProfilePic={matchedUserData?.profilePic}
      audioLevel={audioLevel}
    />
  );
};
